import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DivIcon } from "leaflet";
import { Marker, Pane, Polyline } from "react-leaflet";

// Actions
import {
  selectMedia,
  unSelectMedia,
} from "../../actions/media-selection/MediaSelectionActions";

// Constants and Utils
import { MapZoom } from "../../../constants/GeneralConstants";
import { CPM } from "../../../constants/PriceMode";
import {
  constructCampaignMediaBean,
  getInitialDatesState,
} from "./MediaContainerUtil";
import { getMediaCPAI } from "../../utils/CpaiUtils";

// Components
import LLMap from "../../../components/map/leaflet-map/LLMap";
import TrafficFlowArrow from "../../../components/map/traffic-flow-arrow/TrafficFlowArrow";
import {
  AdditionalButtonState,
  DOOHMediaInfo,
  NonDOOHMediaInfo,
  MediaImageSection,
  MediaTag,
} from "./MediaContainerCells";
import { PlainDropdown } from "../../../components/dropdown/Dropdown";

function MediaDetails({
  roadStretchId,
  media = {},
  cityPlan = {},
  tag,
  openMediaId = "",
}) {
  const dispatch = useDispatch();
  const { campaignId, cityId } = useParams();

  // State to store the priceMode( CPM or FIXED) selected
  const [priceMode, setPriceMode] = useState(CPM);

  // State to store the input buyerPrice
  const [buyerPrice, setBuyerPrice] = useState("");

  // State to store the input sellerPrice
  const [sellerPrice, setSellerPrice] = useState("");

  // number of spot per slot (DIGITAL Media)
  // slot ==> max 3min
  // spot ==> min (1), max (18) [1 spot ==> 10sec]
  const [noOfSpotsPerSlot, setNoOfSpotsPerSlot] = useState(1);

  // media data
  const { mediaId, isDigital = false } = media || {};

  // plan data
  const { roadStretchOtsMap = {}, targetGroupId } = cityPlan || {};
  const roadStretchOtsSplit = roadStretchOtsMap[roadStretchId] || {};

  const campaignMedia = useSelector(
    (state) => state.mediaSelection.mediaIdToCampaignMedia[mediaId]
  );

  // State for storing the media available dates
  const [dateObj, setDuration] = useState(
    getInitialDatesState(campaignMedia, cityPlan)
  );

  const isMediaSelected = useSelector(
    (state) => state.mediaSelection.selectedMedia[mediaId]
  );

  // to disable the add and remove media button when the api call is in pending status
  const selectUnSelectMediaLoadingMap = useSelector(
    (state) => state.mediaSelection.selectUnSelectMediaLoading
  );
  const selectUnSelectMediaLoading = selectUnSelectMediaLoadingMap[mediaId];
  const isAnyMediaLoadingIsTrue = Object.keys(
    selectUnSelectMediaLoadingMap
  ).some((mediaId) => selectUnSelectMediaLoadingMap[mediaId] === true);

  // update initial noOfSpotsPerSlot from campaignMedia
  useEffect(() => {
    const { noOfSpotsPerSlot: mediaNoOfSpotsPerSlot } = campaignMedia || {};
    if (mediaNoOfSpotsPerSlot > 1) {
      setNoOfSpotsPerSlot(mediaNoOfSpotsPerSlot);
    }
  }, []);

  // Media CPAI
  const mediaCpai = getMediaCPAI(media, roadStretchOtsSplit, targetGroupId);

  // Function which initiates when media is selected
  function addMedia() {
    const campaignMediaBean = constructCampaignMediaBean(
      dateObj,
      media,
      priceMode,
      sellerPrice,
      buyerPrice,
      roadStretchId,
      noOfSpotsPerSlot
    );
    dispatch(selectMedia(campaignId, cityId, campaignMediaBean, true));
  }

  // Function which initiates when media is unSelected
  function removeMedia() {
    dispatch(unSelectMedia(campaignId, campaignMedia, mediaId));
  }

  // only show opened media
  if (openMediaId !== mediaId) {
    return null;
  }

  return (
    <div className="d-flex align-items-stretch">
      {/* Media Tag and Information */}
      <div className="col-6 px-0 d-flex align-items-center justify-content-center">
        <MediaImageSection
          media={media}
          className="border rounded-lg media-selection-image"
        />
      </div>

      <div className="col-6 pr-0">
        <MediaTag media={media} tag={tag} />

        {/* DOOH & Non-DOOH Media Info */}
        {!isDigital && (
          <NonDOOHMediaInfo
            media={media}
            duration={dateObj}
            setDuration={setDuration}
            mediaCpai={mediaCpai}
            roadStretchOtsSplit={roadStretchOtsSplit}
          />
        )}
        {isDigital && (
          <DOOHMediaInfo
            media={media}
            duration={dateObj}
            isMediaSelected={isMediaSelected}
            noOfSpotsPerSlot={noOfSpotsPerSlot}
            setNoOfSpotsPerSlot={setNoOfSpotsPerSlot}
            mediaCpai={mediaCpai}
          />
        )}

        <AdditionalButtonState
          isMediaSelected={isMediaSelected}
          selectMedia={addMedia}
          unSelectMedia={removeMedia}
          selectUnSelectMediaLoading={selectUnSelectMediaLoading}
          disabled={isAnyMediaLoadingIsTrue}
        />
      </div>
    </div>
  );
}

function StretchLine({ stretchPoints }) {
  const pathOptions = { color: "orange", weight: 10 };
  return (
    <Polyline pathOptions={pathOptions} positions={stretchPoints}></Polyline>
  );
}

function getMediaIconWithTag(
  selectedMediasMap = {},
  mediaId,
  tag,
  roadMedia = []
) {
  const mediaIcons = roadMedia.reduce((acc, eachMedia) => {
    const { id } = eachMedia;
    const isMediaSelected = selectedMediasMap?.[mediaId];
    const clsStr = isMediaSelected ? "bg-success" : "bg-danger";

    acc[id] = new DivIcon({
      className:
        clsStr +
        " media-tag d-flex align-items-center justify-content-center text-white",
      html: tag,
    });
    return acc;
  }, {});

  return mediaIcons[mediaId];
}

function getMapCenterZoom(trace = []) {
  const traceLength = trace.length;

  // middle of trace points
  const middleIndex = Math.floor(traceLength / 2);
  const centerPoint = trace[middleIndex];

  // center
  const center = {
    latitude: centerPoint[0],
    longitude: centerPoint[1],
  };

  // zoom level
  const { zoomLevel13, zoomLevel14, zoomLevel15 } = MapZoom || {};

  let zoom = zoomLevel14; // default
  if (traceLength <= 10) {
    zoom = zoomLevel15;
  } else if (traceLength >= 25) {
    zoom = zoomLevel13;
  }

  return { center, zoom };
}

function MediaLabelWithTag({ title, tag, tagBgColor }) {
  return (
    <div className="d-flex align-items-center">
      <div className={`media-tag mr-2 text-center shrink-0 ${tagBgColor}`}>
        <b className="text-white">{tag}</b>
      </div>
      <span className="text-truncate-3" title={title}>
        {title}
      </span>
    </div>
  );
}

function MediaSelectDropdown({
  roadMedias = [],
  openMediaId,
  setOpenMediaId = () => {},
}) {
  const mediaSelectedMap = useSelector(
    (state) => state.mediaSelection.selectedMedia
  );

  const mediaListForDropdown = roadMedias.map((media, index) => {
    const { mediaId, title } = media;
    const tag = index + 1;
    const isMediaSelected = mediaSelectedMap[mediaId];
    const tagBgColor = isMediaSelected ? "bg-success" : "bg-danger";

    const mediaLabelWithTag = (
      <MediaLabelWithTag title={title} tag={tag} tagBgColor={tagBgColor} />
    );

    return {
      id: mediaId,
      label: mediaLabelWithTag,
    };
  });

  return (
    <div className="map-media-select-dropdown">
      <PlainDropdown
        items={mediaListForDropdown}
        className="btn px-2 border shadow-none rounded-lg border-primary"
        dropMenuClassName="dropdown-fixed-height"
        placeHolder="Select Media"
        onItemSelect={(id) => setOpenMediaId(id)}
        selectedId={openMediaId}
      />
    </div>
  );
}

/**
 * Media Container
 */
export default function MediaContainer({
  roadInfo = {},
  cityPlan = {},
  roadMedias = [],
}) {
  // State
  const [openMediaId, setOpenMediaId] = useState("");

  // selectedMediaBooleanMap : { mediaId: true, mediaId: false }
  const selectedMediaBooleanMap = useSelector(
    (state) => state.mediaSelection.selectedMedia
  );

  const { trace = [], id } = roadInfo || {};

  // road media ids
  const firstRoadMedia = roadMedias?.[0] || {};
  const firstRoadMediaId = firstRoadMedia?.mediaId || "";

  // update open media id to open first media by default
  useEffect(() => {
    setOpenMediaId(firstRoadMediaId);
  }, [id]);

  // if no road medias present
  if (!roadMedias || roadMedias.length < 1) {
    return null;
  }

  const { center, zoom } = getMapCenterZoom(trace);

  return (
    <div className="d-flex bg-alt">
      <div className="col-4 px-0">
        <MediaSelectDropdown
          roadMedias={roadMedias}
          openMediaId={openMediaId}
          setOpenMediaId={setOpenMediaId}
        />

        <LLMap center={[center.latitude, center.longitude]} zoom={zoom}>
          <StretchLine stretchPoints={trace} />
          <TrafficFlowArrow trace={trace} />

          {roadMedias.map((eachMedia, index) => {
            const { mediaId, latitude, longitude, sourceId } = eachMedia;
            const mediaTag = index + 1;
            const mediaIcon = getMediaIconWithTag(
              selectedMediaBooleanMap,
              mediaId,
              mediaTag,
              roadMedias
            );

            const isOpenMedia = openMediaId === mediaId;
            const markerTitle = `Device ID : ${sourceId || "--"}`;

            // for open media (higher z-index) to show overlapped marker
            if (isOpenMedia) {
              return (
                <Pane
                  key={`openMedia-${mediaId}`}
                  name="openMediaPane"
                  style={{ zIndex: 1000 }}
                >
                  <Marker
                    key={mediaId}
                    title={markerTitle}
                    icon={mediaIcon}
                    position={[latitude, longitude]}
                    eventHandlers={{
                      click: () => setOpenMediaId(mediaId),
                    }}
                  />
                </Pane>
              );
            }

            return (
              <Marker
                key={mediaId}
                title={markerTitle}
                icon={mediaIcon}
                position={[latitude, longitude]}
                eventHandlers={{
                  click: () => setOpenMediaId(mediaId),
                }}
              />
            );
          })}
        </LLMap>
      </div>

      <div className="col-8 p-3 cont-media-selection">
        {roadMedias.map((eachMedia, index) => (
          <MediaDetails
            key={eachMedia.id}
            media={eachMedia}
            cityPlan={cityPlan}
            roadStretchId={roadInfo.id}
            tag={index + 1}
            openMediaId={openMediaId}
          />
        ))}
      </div>
    </div>
  );
}
